import clsx from 'clsx';
import Lottie from 'lottie-react';
import { LottieRefCurrentProps } from 'lottie-react';
import { Suspense, useEffect, useRef } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';

import { Footer } from './Footer';

import logoImg from '@/assets/logo.svg';
import rocketAnimation from '@/assets/menu_rocketAnimation.json';
import { ContactSection } from '@/components/ContactSection';
import { Spinner } from '@/components/Spinner';
import useScrollToTop from '@/hooks/useScrollToTop';
import './MainLayout.css';
import { scrollToElement } from '@/utils/scroll-to-element';

export const MainLayout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const location = useLocation();
  const buttonColor = location.pathname === '/contact' ? 'bg-hoverButton' : 'bg-black';
  const isOnContactPage = location.pathname === '/contact';
  const hoverButtonColor = isOnContactPage ? 'hover:text-white' : 'hover:text-white';
  const getLinkClass = (path: string) => {
    return location.pathname === path ? 'text-button font-bold' : 'hover:text-button';
  };
  const { t } = useTranslation('navigation');
  const scrollRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  useScrollToTop(scrollRef);

  useEffect(() => {
    if (scrollRef.current) {
      setScrollEl(scrollRef.current);
    }
  }, []);

  useEffect(() => {
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (!isOpen) return;

      const target = e.target as Node;
      const clickedOutsideMenu = menuRef.current && !menuRef.current.contains(target);
      const clickedOutsideToggle = toggleBtnRef.current && !toggleBtnRef.current.contains(target);

      if (clickedOutsideMenu && clickedOutsideToggle) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const playRandomly = () => {
      const randomDelay = Math.random() * (60000 - 30000) + 30000;

      timeoutId = setTimeout(() => {
        lottieRef.current?.goToAndPlay(0, true);
        playRandomly();
      }, randomDelay);
    };

    playRandomly();

    return () => clearTimeout(timeoutId); // Clean up on unmount
  }, []);

  return (
    <div className="h-screen w-auto mx-0 flex overflow-hidden">
      <div className="relative flex flex-col w-0 flex-1 overflow-hidden">
        <div className="navbar">
          <div
            className={clsx(
              'z-40 fixed top-0 left-0 w-full navbar bg-white flex items-center justify-between px-10 2xl:px-20 text-lg',
              { 'shadow-2xl': !isOpen }
            )}
          >
            <div className="flex items-center">
              <Link to="/">
                <img src={logoImg} className="w-[90px] min-w-[90px]" alt="Intechnity"></img>
              </Link>
            </div>

            <div className="hidden md:block">
              <Lottie
                lottieRef={lottieRef}
                animationData={rocketAnimation}
                loop={false}
                autoplay={false}
                className="navbar-animation"
              />
            </div>

            <div className="z-40 flex">
              <div className="lg:hidden flex items-center">
                <button ref={toggleBtnRef} onClick={toggleMenu} className="text-black relative">
                  <div
                    className={`transition-transform duration-300 ease-in-out ${
                      isOpen ? 'rotate-180 opacity-0' : 'rotate-0 opacity-100'
                    }`}
                  >
                    <FaBars size={24} />
                  </div>
                  <div
                    className={`transition-transform duration-300 ease-in-out absolute top-0 left-0 ${
                      isOpen ? 'rotate-0 opacity-100' : 'rotate-180 opacity-0'
                    }`}
                  >
                    <FaTimes size={24} />
                  </div>
                </button>
              </div>

              <div className="hidden lg:flex text-gray-400 xl:text-base 2xl:text-lg text-center gap-16">
                <div className="flex justify-center items-center h-10">
                  <Link
                    to="/"
                    className={`uppercase whitespace-nowrap ${getLinkClass('/')}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('home')}
                  </Link>
                </div>

                <div className="flex justify-center items-center h-10">
                  <Link
                    to="/services"
                    className={`uppercase whitespace-nowrap ${getLinkClass('/services')}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('services')}
                  </Link>
                </div>

                <div className="flex justify-center items-center h-10">
                  <Link
                    to="/who-we-are"
                    className={`uppercase whitespace-nowrap ${getLinkClass('/who-we-are')}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('whoWeAre')}
                  </Link>
                </div>

                <div className="flex justify-center items-center h-10">
                  <Link
                    to="/portfolio"
                    className={`uppercase whitespace-nowrap ${getLinkClass('/portfolio')}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('portfolio')}
                  </Link>
                </div>

                <div className="flex justify-center items-center h-10">
                  <Link
                    to="/contact"
                    className={`py-3 px-8 whitespace-nowrap ${buttonColor} text-white ${hoverButtonColor} font-medium rounded-xl uppercase relative contact-button`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('contact')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <div
            className={clsx('navbar-menu', {
              'navbar-menu-open': isOpen,
            })}
            ref={menuRef}
          >
            <div className="flex flex-col items-center text-gray-400 pr-10">
              <Link
                to="/"
                className={`px-3 py-2 uppercase ${getLinkClass('/')}`}
                onClick={() => setIsOpen(false)}
              >
                {t('home')}
              </Link>

              <Link
                to="/services"
                className={`px-3 py-2 uppercase ${getLinkClass('/services')}`}
                onClick={() => setIsOpen(false)}
              >
                {t('services')}
              </Link>

              <Link
                to="/who-we-are"
                className={`px-3 py-2 uppercase ${getLinkClass('/who-we-are')}`}
                onClick={() => setIsOpen(false)}
              >
                {t('whoWeAre')}
              </Link>

              <Link
                to="/portfolio"
                className={`px-3 py-2 uppercase ${getLinkClass('/portfolio')}`}
                onClick={() => setIsOpen(false)}
              >
                {t('portfolio')}
              </Link>

              <button
                className={`bg-black text-white hover:border-4 border-emerald-800 rounded-lg px-3 py-2 my-5 uppercase ${buttonColor} ${hoverButtonColor}`}
                onClick={() => {
                  setIsOpen(false);
                  scrollToElement('contact-section');
                }}
              >
                {t('contact')}
              </button>
            </div>
          </div>
        </div>

        <main
          className="flex flex-1 flex-col relative overflow-y-auto focus:outline-none"
          ref={scrollRef}
        >
          {scrollEl && (
            <ParallaxProvider scrollContainer={scrollRef.current}>
              <Suspense
                fallback={
                  <div className="h-full w-full flex items-center justify-center">
                    <Spinner size="xl" />
                  </div>
                }
              >
                <div className="relative bg-white">
                  <div
                    className={`absolute inset-0 duration-300 lg:hidden ${isOpen && 'bg-black/50'} pointer-events-none z-20`}
                  ></div>

                  <Outlet />
                  <ContactSection />
                </div>
                <div className="relative">
                  <div
                    className={`absolute inset-0 duration-300 lg:hidden ${isOpen && 'bg-black/50'} pointer-events-none z-20`}
                  ></div>
                  <Footer mainRef={scrollRef} />
                </div>
              </Suspense>
            </ParallaxProvider>
          )}
        </main>
      </div>
    </div>
  );
};
